import React, { useEffect, useContext, useCallback } from 'react';
import SeeItem from '../../pieces/SeeItem';
import { useDispatch, useSelector } from 'react-redux';
import { getTypology } from '../../../../store/actions/typologiesActions';
import PropTypes from 'prop-types';
import LabelAndValue from '../../../common/forms/LabelAndValue';
import { TypologyDispatchContext } from './CreateEditTypologyProvider';
import EditTypology from './EditTypology';
import { ConfigStateContext, ConfigDispatchContext } from '../../MenuConfigProvider';
import LabelAndHelper from '../../../common/forms/LabelAndHelper';
import BoxList from '../../../common/BoxList';
import { Box } from '@material-ui/core';
import ThemeList from '../../../common/ThemeList';

const formatTime = (seconds) => {
  if (!seconds || seconds <= 0) return '0 min';

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  const parts = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes} min`);
  if (secs > 0 && hours === 0) parts.push(`${secs}s`);

  return parts.length > 0 ? parts.join(' ') : '0 min';
};

const formatPeriod = (period, ignoreTime = false) => {
  if (!period || !period.enabled) return 'Período desativado.';

  let timeStr = '';

  if (!ignoreTime) {
    if (period.time_enabled) {
      timeStr = formatTime(period.time);
    } else {
      timeStr = 'Timer desativado.';
    }
  }

  const title = period.title ? `"${period.title}"` : '';

  const parts = [timeStr, title].filter(Boolean);

  if (parts.length === 0) return 'Período ativado.';

  return parts.join(' - ');
};

const SeeEditTypology = ({ info }) => {
  const dispatchRedux = useDispatch();
  const dispatch = useContext(TypologyDispatchContext);
  const dispatchConfig = useContext(ConfigDispatchContext);
  const { typology } = useSelector(state => state.typologies);
  const { editing } = useContext(ConfigStateContext);

  useEffect(() => {
    if (info.isItemTypology) {
      dispatchRedux(getTypology(info.id));
    }
    return () => {
      dispatchRedux({
        type: 'CLEAR_TYPOLOGY',
      });
    };
  }, [dispatchRedux, info]);

  useEffect(() => {
    dispatch({
      type: 'UPDATE_FIELD',
      payload: {
        name: 'name',
        value: info.name,
      },
    });
  }, [info.name, dispatch]);

  useEffect(() => {
    dispatch({
      type: 'UPDATE_FIELD',
      payload: {
        name: 'id',
        value: info.id,
      },
    });
  }, [info.id, dispatch]);

  useEffect(() => {
    dispatch({
      type: 'UPDATE_FIELD',
      payload: {
        name: 'type',
        value: typology.is_group ? 'grupo' : 'individual',
      },
    });
  }, [typology.is_group, dispatch]);

  useEffect(() => {
    dispatch({
      type: 'UPDATE_FIELD',
      payload: {
        name: 'private_voting',
        value: typology.private_voting,
      },
    });
  }, [typology.private_voting, dispatch]);

  useEffect(() => {
    dispatch({
      type: 'UPDATE_FIELD',
      payload: {
        name: 'color',
        value: typology.color,
      },
    });
  }, [typology.color, dispatch]);

  useEffect(() => {
    dispatch({
      type: 'UPDATE_FIELD',
      payload: {
        name: 'locals',
        value: typology.locals.map(option => ({
          id: option.id,
          label: option.name,
          value: option.id,
          name: option.name,
        })),
      },
    });
  }, [typology.locals, dispatch]);

  useEffect(() => {
    dispatch({
      type: 'UPDATE_FIELD',
      payload: {
        name: 'themes',
        value: typology.themes
          ? typology.themes.map(theme =>
            theme.points && theme.points.length > 0 ? theme : { ...theme, points: [] }
          )
          : [],
      },
    });
  }, [typology.themes, dispatch]);

  useEffect(() => {
    dispatch({
      type: 'UPDATE_FIELD',
      payload: {
        name: 'points',
        value: typology.points ? typology.points : [],
      },
    });
  }, [typology.points, dispatch]);

  useEffect(() => {
    dispatch({
      type: 'UPDATE_FIELD',
      payload: {
        name: 'parent',
        value: typology.parent
          ? { id: typology.parent.id, name: typology.parent.name }
          : null,
      },
    });
  }, [typology.parent, dispatch]);

  useEffect(() => {
    dispatch({
      type: typology.is_group ? 'SET_GROUPS' : 'SET_INDIVIDUALS',
      payload: (typology.is_group ? typology.groups : typology.users) || [],
    });
  }, [typology.is_group, typology.groups, typology.users, dispatch]);

  useEffect(() => {
    dispatch({
      type: 'UPDATE_FIELD',
      payload: {
        name: 'stratifiedThemes',
        value: typology.themes && typology.themes.length > 0,
      },
    });
    dispatch({
      type: 'UPDATE_FIELD',
      payload: {
        name: 'stratifiedPoints',
        value: typology.points && typology.points.length > 0,
      },
    });
  }, [typology.points, typology.themes, dispatch]);

  useEffect(() => {
    dispatch({
      type: 'UPDATE_FIELD',
      payload: {
        name: 'periods',
        value: typology.periods || {
          publicDiscussion: { title: '', enabled: true, time_enabled: true, time: 5 * 60 },
          beforeDayOrder: { title: '', enabled: true, time_enabled: true, time: 0 },
          presentation: { title: '', enabled: true, time_enabled: true, time: 3 * 60 },
          protests: { title: '', enabled: true, time_enabled: true, time: 3 * 60 },
        },
      },
    });
  }, [typology.periods, dispatch]);

  const startEdit = useCallback(
    e => {
      e.preventDefault();
      dispatchConfig({
        type: 'SET_EDITING',
      });
    },
    [dispatchConfig]
  );

  if (editing) {
    return <EditTypology />;
  }

  if (typology.id) {
    const periods = typology.periods || {};

    return (
      <SeeItem submitEdit={startEdit}>
        <LabelAndValue label="Nome" value={typology.name} />
        <LabelAndValue
          label="Tipologia Superior"
          value={(info.parent && info.parent.name) || 'Sem Tipologia Superior.'}
        />
        <LabelAndValue
          label="Natureza da tipologia"
          value={typology.is_group ? 'Grupos' : 'Utilizadores individuais'}
        />
        <LabelAndValue
          label="Tipo de votação"
          value={
            typology.private_voting
              ? 'Permitir escolha entre votação secreta e pública'
              : 'Apenas votação pública'
          }
        />
        <LabelAndHelper
          tertiary
          label="Participantes"
          helper="Elementos carregados por predefinição para reuniões desta tipologia."
        />
        <Box height="20px" />
        <BoxList items={typology.is_group ? typology.groups : typology.users} />
        <Box height="8px" />
        {typology.themes && typology.themes.length > 0 && (
          <>
            <LabelAndHelper
              tertiary
              label="Temas"
              helper="Temas predefinidos para reuniões desta tipologia."
            />
            <Box height="20px" />
            <ThemeList themes={typology.themes} isGroup={Boolean(typology.is_group)} />
            <Box height="8px" />
          </>
        )}
        {typology.points && typology.points.length > 0 && (
          <>
            <LabelAndHelper
              tertiary
              label="Pontos de discussão"
              helper="Pontos de discussão predefinidos para reuniões desta tipologia."
            />
            <Box height="20px" />
            <BoxList items={typology.points} />
            <Box height="8px" />
          </>
        )}
        <LabelAndHelper
          tertiary
          label="Configuração de períodos"
          helper="Períodos configurados para reuniões desta tipologia."
        />
        <Box height="20px" />
        <LabelAndValue
          label="Intervenção do público"
          value={formatPeriod(periods.publicDiscussion)}
        />
        <LabelAndValue
          label="Antes da ordem do dia"
          value={formatPeriod(periods.beforeDayOrder, true)}
        />
        <LabelAndValue
          label="Apresentação da proposta"
          value={formatPeriod(periods.presentation)}
        />
        <LabelAndValue
          label="Protestos / defesa de honra / reações"
          value={formatPeriod(periods.protests)}
        />
      </SeeItem>
    );
  }

  return null;
};

SeeEditTypology.propTypes = {
  info: PropTypes.object.isRequired,
};

export default React.memo(SeeEditTypology);
